import React, { useContext, useEffect, useState } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
	Tab, Tabs, TabList, TabPanel
} from 'react-tabs'
import { differenceInDays, format, isBefore } from 'date-fns'
import { emerald, yellow, red } from 'tailwindcss/colors'
import { FiCheck, FiEdit, FiFileText, FiGitCommit, FiLayers, FiUsers } from 'react-icons/fi'

import { Container } from './styles'
import Card from '../../../../components/Card'
import { api, authHeaders } from '../../../../services/api'
import { concatFormacoes } from '../../../UserViews/EspacoMedico/DadosCadastrais'
import { TimelineTRL } from './TimelineTRL'
import Spinner from '../../../../components/Spinner'
import { GlobalContext } from '../../../../contexts/GlobalContext'
import { DadosProjeto } from './DadosProjeto'
import arrayUtils from '../../../../util/array'
import { EtapasProjeto } from './EtapasProjeto'
import { ConviteProjeto } from './ConviteProjeto'
import { pluralize } from '../../../../util/string'
import useQuery from '../../../../hooks/useQuery'
import { Button } from '../../../../components/Form'
import { downloadFileFromBlob } from '../../../../util/file'
import { TipContext } from '../../../../contexts/TipContext'
import { loadTips } from '../../../../util/tip'
import formUtils from '../../../../util/form'
import { EditInPlace } from '../../../../components/EditInPlace'
import { AceiteParticipacao } from './AceiteParticipacao'
import { PainelProjetoRepresentante } from './PainelProjetoRepresentante'
import { concatenarEndereco } from '../../../../util/endereco'

export default function () {
	const { user, checkException } = useContext(GlobalContext)
	const { setCodigo } = useContext(TipContext)

	const { id } = useParams()
	const { etapa: etapaId } = useQuery()
	const history = useHistory()
	const { state } = useLocation()

	const [projeto, setProjeto] = useState(null)
	const [tiposDocumento, setTiposDocumento] = useState(null)
	const [selectedTab, setSelectedTab] = useState(0)
	const [exportandoPDF, setExportandoPDF] = useState(false)
	const etapaAbertaMemoState = useState(null)
	const etapaPessoaAbertaMemoState = useState(null)
	const [funcoes, setFuncoes] = useState(null)

	const [etapaAbertaMemo, setEtapaAbertaMemo] = etapaAbertaMemoState
	const [etapaPessoaAbertaMemo, setEtapaPessoaAbertaMemo] = etapaPessoaAbertaMemoState

	async function loadProjeto() {
		setProjeto(null)

		const response = await api.get(`projeto/${id}`, {
			params: {
				scope: 'projeto'
			},
			...authHeaders()
		})

		const projetoPessoas = [
			...response.projeto_pessoas.filter(pp => pp.aceito === null),
			...response.projeto_pessoas.filter(pp => pp.aceito === true),
			...response.projeto_pessoas.filter(pp => pp.aceito === false)
		]

		response.pessoas = projetoPessoas.map(proj_pessoa => {
			const formacoes = proj_pessoa.pessoa.formacoes?.reduce((result, formacao) => {
				return [
					...result,
					`${formacao.grau.descricao} em ${concatFormacoes(formacao.formacao)}`
				]
			}, [])

			return {
				...proj_pessoa.pessoa,
				condicao: proj_pessoa.funcao.descricao,
				aceite: proj_pessoa.aceito,
				formacao: formacoes?.join('<br/>')
			}
		})

		response.documentos = response.documentos.map(doc => ({
			id: doc.id,
			tipo: doc.tipo_documento.descricao,
			descricao: doc.descricao,
			link: doc.arquivo?.link
		}))

		let etapas = response.etapas.map(item => {
			let cor

			if (isBefore(new Date(item.fim), new Date())) {
				cor = red[200]
			} else if (differenceInDays(new Date(item.fim), new Date()) <= 3) {
				cor = yellow[100]
			} else {
				cor = emerald[100]
			}

			return {
				...item,
				inicio_formatado: format(new Date(item.inicio), 'dd/MM/yyyy'),
				fim_formatado: format(new Date(item.fim), 'dd/MM/yyyy'),
				equipe_quantidade_pessoas: `${pluralize(item.projeto_etapa_pessoas.length, 'pessoa', null, 'Nenhuma')}`,
				percentual_conclusao: `${item.percentualConclusao}%`,
				trl: `Nível ${item.trl.nivel}`,
				background: cor,
				projeto_etapa_pessoas: item.projeto_etapa_pessoas.map(item => ({
					...item,
					condicao: projetoPessoas.find(p => p.pessoa.id === item.pessoa.id).funcao
				}))
			}
		})

		etapas = etapas.sort(arrayUtils.sort.comparisonFunction('id', 'asc'))

		response.etapas = etapas

		response.locais = response.locais.map(local => ({
			...local,
			endereco_string: concatenarEndereco(local.endereco)
		}))

		delete response.projeto_pessoas

		setProjeto(response)

		if (etapaAbertaMemo) {
			setEtapaAbertaMemo(response.etapas.find(e => e.id === etapaAbertaMemo.id))
		}

		if (etapaPessoaAbertaMemo) {
			setEtapaPessoaAbertaMemo(response.etapas.find(e => e.id === etapaAbertaMemo.id)?.projeto_etapa_pessoas?.find(e => e.id === etapaPessoaAbertaMemo.id)?.projeto_pessoa?.pessoa?.id)
		}
	}

	async function loadTiposDocumento() {
		const response = await api.get('tipo_documento', {
			params: {
				grupo: 'projeto'
			}
		})

		setTiposDocumento(response)
	}

	async function handleExportarPDF() {
		try {
			setExportandoPDF(true)

			const response = await api.get(`projeto/${projeto.id}/exportar_pdf`, {
				...authHeaders(),
				responseType: 'blob'
			})

			downloadFileFromBlob(response, { extension: 'pdf' })
		} catch (e) {
			checkException(e)
		} finally {
			setExportandoPDF(false)
		}
	}

	async function loadFuncoes() {
		const response = await api.get('funcao', authHeaders())

		setFuncoes(response.filter(funcao => !funcao.descricao.includes('TRL')))
	}

	async function handleUpdateCampo(values) {
		try {
			values = formUtils.extractFormValues(values)

			await api.put(`projeto/${projeto.id}`, values, authHeaders())

			loadProjeto()
		} catch (e) {
			checkException(e)
		}
	}

	useEffect(() => {
		if (state?.aba_selecionada_index) {
			setSelectedTab(+state.aba_selecionada_index)
		}
	}, [state])

	useEffect(() => {
		loadTips(setCodigo, 'form_projeto_dados_projeto')

		loadProjeto()

		loadTiposDocumento()

		loadFuncoes()
	}, [])

	useEffect(() => {
		if (etapaId && projeto?.etapas.length) {
			const etapaDaURL = projeto.etapas.find(etp => etp.id === +etapaId)

			setSelectedTab(2)
			setEtapaAbertaMemo(etapaDaURL)
		}
	}, [projeto, etapaId])

	if (user.perfil.id === 3) { // Representante de empresa e não é avaliador do projeto em questão
		return (
			<Container>
				<Card>
					<PainelProjetoRepresentante
						projeto={projeto}
						onChangeAvaliadorTRL={loadProjeto}
					/>
				</Card>
			</Container>
		)
	}

	if (!projeto || !tiposDocumento) {
		return (
			<Container>
				<Card>
					<Spinner />
				</Card>
			</Container>
		)
	}

	const podeVisualizarProjeto = projeto.pessoas.some(p => p.id === user.pessoa.id)

	if (!podeVisualizarProjeto) {
		history.goBack()

		toast.error('Desculpe, você não participa deste projeto e não pode acessá-lo.')

		return
	}

	const condicao = projeto.pessoas.find(p => p.id === user.pessoa.id).condicao || ''

	const eExecutor = ['autor', 'co-autor'].includes(condicao.toLowerCase())
	const eAvaliador = condicao.toLowerCase() === 'avaliador trl'

	const aceiteParticipacao = projeto.pessoas.some(pessoa => pessoa.aceite && pessoa.id === user.pessoa.id)

	if (!aceiteParticipacao) {
		return <AceiteParticipacao onAceitar={loadProjeto} />
	}

	return (
		<Container>
			<Card className="flex flex-col lg:grid lg:grid-cols-[2fr_1fr] gap-x-6">
				<div className="flex gap-2 items-center text-xl font-medium">
					<span>Projeto: </span>

					{eExecutor ? (
						<EditInPlace name="nome" onUpdate={handleUpdateCampo} originalValue={projeto.nome}>
							{() => (
								<div className="flex gap-4 group items-center">
									<h1 className="group-hover:border-b group-hover:border-dashed">
										{projeto.nome}
									</h1>

									<FiEdit size={20} className="hover:cursor-pointer" />
								</div>
							)}
						</EditInPlace>
					) : (
						<h1 className="group-hover:border-b group-hover:border-dashed">
							{projeto.nome}
						</h1>
					)}
				</div>

				<Tabs
					selectedIndex={selectedTab}
					onSelect={setSelectedTab}
					className="react-tabs horizontal-tabs col-span-2 mt-8"
				>
					<TabList className="flex flex-row border-b border-white/50">
						<Tab>
							<div className="flex items-center gap-2">
								<FiLayers size={18} />
								Dados do projeto
							</div>
						</Tab>

						<Tab>
							<div className="flex items-center gap-2">
								<FiCheck size={19} />
								TRL do projeto
							</div>
						</Tab>

						{!eAvaliador && (
							<>
								<Tab>
									<div className="flex items-center gap-2">
										<FiGitCommit size={19} />
										MRL do projeto
									</div>
								</Tab>

								<Tab>
									<div className="flex items-center gap-2">
										<FiUsers size={18} />
										Convidar pessoas
									</div>
								</Tab>

								<Tab>
									<div className="flex items-center gap-2">
										<FiFileText size={17} />
										Exportar
									</div>
								</Tab>
							</>
						)}
					</TabList>

					<TabPanel>
						<DadosProjeto
							id={id}
							projeto={projeto}
							loadProjeto={loadProjeto}
							tiposDocumento={tiposDocumento}
							funcoes={funcoes}
							handleUpdateCampo={handleUpdateCampo}
						/>
					</TabPanel>

					<TabPanel>
						<div className="animated fadeIn fast">
							<TimelineTRL
								projeto={projeto}
								permissoes={{
									eExecutor,
									eAvaliador
								}}
							/>
						</div>
					</TabPanel>

					{!eAvaliador && (
						<>
							<TabPanel>
								<div className="animated fadeIn fast">
									<EtapasProjeto
										projeto={projeto}
										loadProjeto={loadProjeto}
										etapaAbertaMemoState={etapaAbertaMemoState}
										etapaPessoaAbertaMemoState={etapaPessoaAbertaMemoState}
									/>
								</div>
							</TabPanel>

							<TabPanel>
								<div className="animated fadeIn fast">
									<ConviteProjeto
										projeto={projeto}
										funcoes={funcoes}
									/>
								</div>
							</TabPanel>

							<TabPanel>
								<div className="animated fadeIn fast flex justify-center pt-8">
									<Button onClick={handleExportarPDF} className="white" loading={exportandoPDF}>
										Exportar PDF
										<FiFileText size={16} />
									</Button>
								</div>
							</TabPanel>
						</>
					)}
				</Tabs>
			</Card>
		</Container>
	)
}
